import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'
import {useToastContext} from '@github-ui/toast/ToastContext'
import {TagIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import type React from 'react'
import {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  graphql,
  type PreloadedQuery,
  readInlineData,
  usePaginationFragment,
  usePreloadedQuery,
  useRelayEnvironment,
} from 'react-relay'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'

import {VALUES} from '../constants/values'
import {ERRORS} from '../constants/errors'
import {HOTKEYS} from '../constants/hotkeys'
import {LABELS} from '../constants/labels'
import {commitUpdateIssueLabelsMutation} from '../mutations/update-issue-labels-mutation'
import {CompressedLabelsToken} from './CompressedLabelsToken'
import type {LabelPickerLabel$data, LabelPickerLabel$key} from './__generated__/LabelPickerLabel.graphql'
import type {LabelPickerLabelsPaginated$key} from './__generated__/LabelPickerLabelsPaginated.graphql'
import type {LabelPickerQuery} from './__generated__/LabelPickerQuery.graphql'
import type {LabelsListPaginationQuery} from './__generated__/LabelsListPaginationQuery.graphql'
import {type ExtendedItemProps, ItemPicker, type SharedBulkActionsItemPickerProps} from './ItemPicker'
import type {ItemGroup} from '../shared'
import {commitSetLabelsForLabelableMutation} from '../mutations/set-labels-for-labelable-mutation'
import {commitUpdateIssueLabelsBulkMutation} from '../mutations/update-issue-labels-bulk-mutation'
import type {updateIssueLabelsBulkMutation$data} from '../mutations/__generated__/updateIssueLabelsBulkMutation.graphql'
import type {updateIssueLabelsBulkByQueryMutation$data} from '../mutations/__generated__/updateIssueLabelsBulkByQueryMutation.graphql'
import {commitUpdateIssueLabelsBulkByQueryMutation} from '../mutations/update-issue-labels-bulk-by-query-mutation'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'
import {useKeyPress} from '@github-ui/use-key-press'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {fuzzyFilter} from '@github-ui/fuzzy-score/fuzzy-filter'

type LabelPickerBaseProps = {
  labelableId?: string
  readonly: boolean
  labels: LabelPickerLabel$data[]
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  onSelectionChanged?: (selectedItems: LabelPickerLabel$data[]) => void
  onIssueUpdate?: () => void
  /**
   * Whether to render the label picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  initialFilter?: string
  shortcutHintVisible?: boolean
}

type LabelPickerProps = LabelPickerBaseProps & {
  labelQueryRef: PreloadedQuery<LabelPickerQuery>
}

type LazyLabelPickerProps = LabelPickerBaseProps & {
  labelableTypeName: 'Issue' | 'PullRequest'
  repo: string
  owner: string
}

type BulkLabelPickerProps = Omit<LabelPickerProps, 'issueId' | 'labels'> & {
  existingIssueLabels: Map<string, string[]>
  labelAppliedToAll: LabelPickerLabel$data[]
  labelAppliedToSome: LabelPickerLabel$data[]
  connectionIds: {[key: string]: string[]}
} & SharedBulkActionsItemPickerProps

type LabelPickerInternalProps = Omit<LabelPickerProps, 'labelQueryRef'> & {
  paginatedKey: LabelPickerLabelsPaginated$key | null
  isLoading: boolean
  onSelectionChange: (labels: LabelPickerLabel$data[]) => void
}

export const LabelPickerGraphqlQuery = graphql`
  query LabelPickerQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      ...LabelPickerLabelsPaginated
    }
  }
`
export const AssignedLabelFragment = graphql`
  fragment LabelPickerAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 20})
  @refetchable(queryName: "LabelPickerAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor) @connection(key: "LabelPicker_labels") {
      edges {
        node {
          ...LabelPickerLabel
        }
      }
    }
  }
`
export const LabelPickerLabelsPaginatedFragment = graphql`
  fragment LabelPickerLabelsPaginated on Repository
  @argumentDefinitions(cursor: {type: "String"}, count: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "LabelsListPaginationQuery") {
    labels(first: $count, after: $cursor, orderBy: {field: NAME, direction: ASC})
      @connection(key: "Repository_labels") {
      edges {
        node {
          ...LabelPickerLabel
        }
      }
      totalCount
    }
  }
`
export const LabelFragment = graphql`
  fragment LabelPickerLabel on Label @inline {
    id
    color
    name
    nameHTML
    description
    # eslint-disable-next-line relay/unused-fields
    url
  }
`

type CompressedLabelAnchorProps = {
  size?: 'small' | 'large'
  labels: LabelPickerLabel$data[]
  anchorProps?: React.HTMLAttributes<HTMLElement>
  displayHotkey: boolean
  MAX_DISPLAYED_LABELS?: number
  readonly?: boolean
}

const CompressedLabelAnchor = forwardRef<HTMLButtonElement, CompressedLabelAnchorProps>(
  ({size = 'small', labels, anchorProps, displayHotkey, MAX_DISPLAYED_LABELS = 2, readonly}, ref) => {
    return (
      <CompressedLabelsToken
        size={size}
        labelNamesWithColor={labels.map(l => ({nameHTML: l.nameHTML, color: l.color}))}
        anchorProps={anchorProps}
        MAX_DISPLAYED_LABELS={MAX_DISPLAYED_LABELS}
        readonly={readonly}
        leadingIcon={TagIcon}
        hotKey={!readonly && displayHotkey ? HOTKEYS.labelPicker.toUpperCase() : undefined}
        anchorText={labels?.length === 0 ? LABELS.noLabels : undefined}
        ref={ref}
      />
    )
  },
)

CompressedLabelAnchor.displayName = 'CompressedLabelAnchor'

const labelsGroup: ItemGroup = {groupId: 'labels'}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}

export function LabelPicker({labelableId, onIssueUpdate, ...props}: LabelPickerProps) {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel$data[]) => {
      commitUpdateIssueLabelsMutation({
        environment,
        input: {issueId: labelableId ?? '', labels: selectedLabels},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLabels,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, labelableId, onIssueUpdate],
  )

  return <LabelPickerBase labelableId={labelableId} onSelectionChange={onSelectionChange} {...props} />
}

export function BulkIssuesLabelPicker({
  issuesToActOn,
  existingIssueLabels,
  labelAppliedToAll,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Will be in use once SelectPanel support partial selection
  labelAppliedToSome,
  connectionIds,
  useQueryForAction,
  repositoryId,
  query,
  onCompleted,
  onError,
  ...props
}: BulkLabelPickerProps) {
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel$data[]) => {
      const labelsToAdd = selectedLabels.filter(l => !labelAppliedToAll.some(la => la.name === l.name.toLowerCase()))
      const labelsToRemove = labelAppliedToAll.filter(la => !selectedLabels.some(l => la.name === l.name.toLowerCase()))
      const args = {
        applyLabelIds: labelsToAdd.map(l => l.id),
        removeLabelIds: labelsToRemove.map(l => l.id),
      }
      const mutationArgs = {
        environment,
        optimisticUpdateIds: issuesToActOn,
        existingIssueLabels,
        connectionIds,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueLabelsBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            ...args,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueLabelsBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueLabelsBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            ...args,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueLabelsBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [
      connectionIds,
      environment,
      existingIssueLabels,
      issuesToActOn,
      labelAppliedToAll,
      onCompleted,
      onError,
      query,
      repositoryId,
      useQueryForAction,
    ],
  )

  return <LabelPickerBase labels={labelAppliedToAll} onSelectionChange={onSelectionChange} {...props} />
}

export function LabelPickerBase({
  labelQueryRef,
  ...rest
}: LabelPickerProps & {onSelectionChange: (labels: LabelPickerLabel$data[]) => void}) {
  const preloadedData = usePreloadedQuery<LabelPickerQuery>(LabelPickerGraphqlQuery, labelQueryRef)

  return preloadedData.repository ? (
    <LabelPickerInternal {...rest} isLoading={false} paginatedKey={preloadedData.repository} />
  ) : null
}

export function LazyLabelPickerOnClick({
  labelableId,
  repo,
  owner,
  readonly,
  shortcutEnabled,
  labels,
  onIssueUpdate,
  anchorElement,
  onSelectionChanged,
}: LazyLabelPickerProps) {
  const [wasTriggered, setWasTriggered] = useState(false)

  useKeyPress(shortcutEnabled ? [HOTKEYS.labelPicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered && anchorElement) {
    return anchorElement({onClick: () => setWasTriggered(true)})
  }

  return (
    <LazyLabelPicker
      labelableId={labelableId}
      repo={repo}
      owner={owner}
      labelableTypeName={'Issue'}
      readonly={readonly}
      shortcutEnabled={shortcutEnabled}
      initialFilter={''}
      labels={labels}
      triggerOpen={true}
      onIssueUpdate={onIssueUpdate}
      onSelectionChanged={onSelectionChanged}
      anchorElement={anchorElement}
    />
  )
}

export function LazyLabelPicker({
  labelableId,
  repo,
  owner,
  labelableTypeName,
  onIssueUpdate,
  onSelectionChanged,
  ...props
}: LazyLabelPickerProps) {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const defaultLabelSelectionChangedAction = useCallback(
    (selectedLabels: LabelPickerLabel$data[]) => {
      commitSetLabelsForLabelableMutation({
        environment,
        input: {labelableId: labelableId ?? '', labels: selectedLabels, labelableTypeName},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLabels,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, labelableId, labelableTypeName, onIssueUpdate],
  )

  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel$data[]) => {
      if (onSelectionChanged) {
        onSelectionChanged(selectedLabels)
      } else {
        defaultLabelSelectionChangedAction(selectedLabels)
      }
    },
    [defaultLabelSelectionChangedAction, onSelectionChanged],
  )

  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<LabelPickerLabelsPaginated$key | null>(null)

  useEffect(() => {
    if (!IS_SERVER) {
      clientSideRelayFetchQueryRetained<LabelPickerQuery>({
        environment,
        query: LabelPickerGraphqlQuery,
        variables: {owner, repo},
      }).subscribe({
        next: internalData => {
          const labels = internalData.repository
          setData(labels ?? null)
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
        },
      })
    }
  }, [environment, fetchKey, owner, repo])

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('labels'),
    anchorElement: props.anchorElement,
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(fetchKey + 1))
  }

  return (
    <LabelPickerInternal
      labelableId={labelableId}
      paginatedKey={data}
      isLoading={isLoading}
      onSelectionChange={onSelectionChange}
      {...props}
    />
  )
}

function LabelPickerInternal({
  readonly,
  labels,
  paginatedKey,
  isLoading,
  onSelectionChange,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  nested = false,
  triggerOpen = false,
  initialFilter,
  shortcutHintVisible = true,
}: LabelPickerInternalProps) {
  const [filter, setFilter] = useState('')

  const {data, loadNext, isLoadingNext, hasNext} = usePaginationFragment<
    LabelsListPaginationQuery,
    LabelPickerLabelsPaginated$key
  >(LabelPickerLabelsPaginatedFragment, paginatedKey)
  const totalNumberOfLabels = data?.labels?.totalCount || 0
  const fetchedLabels = (data?.labels?.edges || []).flatMap(a =>
    // eslint-disable-next-line no-restricted-syntax
    a?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, a.node)] : [],
  )

  useEffect(() => {
    if (hasNext && !isLoadingNext && fetchedLabels.length < VALUES.labelsMaxPreloadCount) {
      loadNext(VALUES.labelsPageSize)
    }
  }, [loadNext, fetchedLabels, totalNumberOfLabels, hasNext, isLoadingNext])

  const items = useMemo(() => {
    const repositoryLabels = fetchedLabels.slice().sort((a, b) => a.name.localeCompare(b.name))
    const sortedSelectedLabels = labels.slice().sort((a, b) => a.name.localeCompare(b.name))
    const allLabels = sortedSelectedLabels.concat(repositoryLabels.filter(l => !labels.find(l2 => l2.id === l.id)))
    if (!filter) return allLabels
    return fuzzyFilter<LabelPickerLabel$data>({
      items: allLabels,
      filter,
      key: (l: LabelPickerLabel$data) => l.name,
      secondaryKey: (l: LabelPickerLabel$data) => `${l.name} ${l.description}`,
    })
  }, [fetchedLabels, filter, labels])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((label: LabelPickerLabel$data) => label.id, [])

  const convertToItemProps = useCallback(
    (label: LabelPickerLabel$data): ExtendedItemProps<LabelPickerLabel$data> => {
      return {
        id: label.id,
        description: label.description ?? undefined,
        descriptionVariant: 'block',
        children: <SafeHTMLText sx={{wordBreak: 'break-word'}} html={label.nameHTML as SafeHTMLString} />,
        source: label,
        groupId: labels.find(l => l.id === label.id) ? labelsGroup.groupId : suggestionsGroup.groupId,
        leadingVisual: () => (
          <Box
            sx={{
              bg: `#${label.color}`,
              borderColor: `#${label.color}`,
              width: 14,
              height: 14,
              borderRadius: 10,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          />
        ),
      }
    },
    [labels],
  )

  const labelPickerRef = useRef<HTMLButtonElement>(null)

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      return (
        <CompressedLabelAnchor
          labels={labels}
          readonly={readonly}
          displayHotkey={shortcutEnabled && shortcutHintVisible}
          anchorProps={readonly ? undefined : anchorProps}
          ref={labelPickerRef}
        />
      )
    },
    [anchorElement, shortcutHintVisible, labels, readonly, shortcutEnabled],
  )

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (labels.length > 0) {
      itemGroups.push(labelsGroup)
    }
    if (items.length > 0 && items.length - labels.length > 0) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [labels, items])

  return (
    <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1}}>
      <ItemPicker
        loading={isLoading}
        items={items}
        initialSelectedItems={labels}
        openHotkey={shortcutEnabled ? HOTKEYS.labelPicker : undefined}
        title={LABELS.labelsHeader}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={`Filter labels ${
          fetchedLabels.length >= VALUES.labelsMaxPreloadCount ? `(${VALUES.labelsMaxPreloadCount} labels loaded)` : ''
        }`}
        selectionVariant="multiple"
        onSelectionChange={onSelectionChange}
        renderAnchor={renderAnchor}
        groups={groups}
        insidePortal={insidePortal}
        height={'large'}
        width={'medium'}
        nested={nested}
        resultListAriaLabel={'Label results'}
        triggerOpen={triggerOpen}
        initialFilter={initialFilter}
        selectPanelRef={labelPickerRef}
      />
    </Box>
  )
}

try{ LabelPicker.displayName ||= 'LabelPicker' } catch {}
try{ BulkIssuesLabelPicker.displayName ||= 'BulkIssuesLabelPicker' } catch {}
try{ LabelPickerBase.displayName ||= 'LabelPickerBase' } catch {}
try{ LazyLabelPickerOnClick.displayName ||= 'LazyLabelPickerOnClick' } catch {}
try{ LazyLabelPicker.displayName ||= 'LazyLabelPicker' } catch {}
try{ LabelPickerInternal.displayName ||= 'LabelPickerInternal' } catch {}